import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { aboutApi } from '../services/api';
import { MediaManager } from './components/MediaManager';
import { MediaPreview } from './components/MediaPreview';

interface MainVisualContent {
  subtitle: string;
  title: string;
  highlightedText: string;
  description: string;
  backgroundVideo?: string;
}

interface MainVisualEditorProps {
  title: string;
}

export const MainVisualEditor: React.FC<MainVisualEditorProps> = ({ title }) => {
  const [content, setContent] = useState<MainVisualContent>({
    subtitle: '',
    title: '',
    highlightedText: '',
    description: '',
    backgroundVideo: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const data = await aboutApi.get();
      if (data && data.mainVisual) {
        setContent(data.mainVisual);
      } else {
        // mainVisual 데이터가 없으면 기본값 사용
        console.warn('mainVisual 데이터가 없습니다. 기본값을 사용합니다.');
        setContent({
          subtitle: '지상파방송ㆍ홍보영상ㆍ마케팅ㆍ공연 기획',
          title: '씨유미디어그룹',
          highlightedText: '재미와 공익을 동시에 충족시키는',
          description: '공중파 프로그램 제작에서 이미 독보적인 영역을 개척하고 한발 더 나아가 차별화된\n다큐멘터리와 기업의 전문적인 홍보영상, 디지털 콘텐츠, 오프라인 관련 사업을 기획 및 제작하고 있습니다.',
          backgroundVideo: '/video/mainvideo.mp4',
        });
      }
    } catch (error) {
      console.error('Main Visual 데이터 로드 실패:', error);
      setErrorMessage('Main Visual 데이터를 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aboutApi.update({ mainVisual: content });
      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (field: keyof MainVisualContent, value: string) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleVideoUpload = (fileUrl: string) => {
    handleContentChange('backgroundVideo', fileUrl);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Main Visual 데이터를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          {title}
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="서브타이틀"
              value={content.subtitle}
              onChange={(e) => handleContentChange('subtitle', e.target.value)}
              variant="outlined"
              multiline
              rows={2}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="메인 타이틀"
              value={content.title}
              onChange={(e) => handleContentChange('title', e.target.value)}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="강조 텍스트"
              value={content.highlightedText}
              onChange={(e) => handleContentChange('highlightedText', e.target.value)}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="설명"
              value={content.description}
              onChange={(e) => handleContentChange('description', e.target.value)}
              variant="outlined"
              multiline
              rows={4}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              배경 비디오
            </Typography>
            
            {content.backgroundVideo && (
              <Box sx={{ mb: 2 }}>
                <MediaPreview
                  src={content.backgroundVideo}
                  alt="배경 비디오"
                  title="메인 비주얼 배경 비디오"
                  type="video"
                  width="100%"
                  height={300}
                />
              </Box>
            )}
            
            <MediaManager
              onMediaSelect={(media) => handleVideoUpload(media.url)}
              filterType="video"
              showUpload={true}
              showPreview={true}
              showEdit={false}
              showDelete={false}
              multiple={false}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSave}
                disabled={saving}
                size="large"
              >
                {saving ? '저장 중...' : '저장'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 성공 알림 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Main Visual 정보가 성공적으로 저장되었습니다.
        </Alert>
      </Snackbar>

      {/* 에러 알림 */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}; 